import Alpine from 'alpinejs';
import axios from '../../config/axios.js';

const DEFAULT_OVERRIDES = {
  hero_badge: '',
  hero_subtitle: '',
  topbar_bg: '',
  topbar_text: '',
  navbar_bg: '',
  navbar_border: '',
  body_bg_type: 'color',
  body_bg_color: '#FFFFFF',
  body_bg_image: '',
  body_bg_gradient: '',
  body_bg_size: 'cover',
  body_bg_repeat: 'no-repeat',
  hero_bg_type: 'color',
  hero_bg_color: '#FFFFFF',
  hero_bg_image: '',
  hero_bg_gradient: '',
};

const DEFAULT_CONFIG = {
  primary_color: '#B22222',
  secondary_color: '#D4AF37',
  accent_color: '#D4AF37',
  background_color: '#FFFFFF',
  surface_color: '#FFFFFF',
  text_color: '#1A1A1A',
  muted_text_color: '#737373',
  border_color: '#ECECEC',
  success_color: '#22C55E',
  warning_color: '#F59E0B',
  danger_color: '#DC2626',
  info_color: '#2563EB',
  border_radius: '12px',
  component_overrides: structuredClone(DEFAULT_OVERRIDES),
};

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInput(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function resolvePreviewHeroBg(overrides = {}, fallback = '#FFFFFF') {
  if (overrides.hero_bg_type === 'gradient' && overrides.hero_bg_gradient) {
    return overrides.hero_bg_gradient;
  }
  if (overrides.hero_bg_type === 'color') {
    return overrides.hero_bg_color || fallback;
  }
  return fallback;
}

function resolvePreviewBodyBg(overrides = {}, fallback = '#FFFFFF') {
  if (overrides.body_bg_type === 'gradient' && overrides.body_bg_gradient) {
    return overrides.body_bg_gradient;
  }
  if (overrides.body_bg_type === 'color') {
    return overrides.body_bg_color || fallback;
  }
  return fallback;
}

function resolvePreviewImage(path) {
  if (!path) return 'none';
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return `url("${path}")`;
  }
  const base = window.APP_URL || '';
  return `url("${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}")`;
}

function createKasirSeasonalThemeBuilder() {
  return {
    themeId: null,
    listUrl: '/',
    editBase: '',
    homeUrl: '/',
    loading: false,
    saving: false,
    uploading: false,
    uploadingBg: false,
    uploadType: 'decoration',
    uploadPosition: '',
    assets: [],
    colorFields: {
      primary_color: 'Primary',
      secondary_color: 'Secondary',
      accent_color: 'Accent',
      background_color: 'Background',
      surface_color: 'Surface',
      text_color: 'Text',
      border_color: 'Border',
    },
    form: {
      name: '',
      slug: '',
      description: '',
      type: 'seasonal',
      status: 'draft',
      priority: 10,
      start_at_local: '',
      end_at_local: '',
      config: structuredClone(DEFAULT_CONFIG),
      effects: [],
    },

    get previewStyle() {
      const c = this.form.config || {};
      const o = c.component_overrides || {};
      const heroBg = resolvePreviewHeroBg(o, c.background_color || '#FFFFFF');
      const bodyBg = resolvePreviewBodyBg(o, c.background_color || '#FFFFFF');
      const heroImage = o.hero_bg_type === 'image' && o.hero_bg_image ? resolvePreviewImage(o.hero_bg_image) : 'none';
      const bodyImage = o.body_bg_type === 'image' && o.body_bg_image ? resolvePreviewImage(o.body_bg_image) : 'none';

      return [
        `--pv-primary:${c.primary_color || '#B22222'}`,
        `--pv-accent:${c.accent_color || '#D4AF37'}`,
        `--pv-bg:${bodyBg}`,
        `--pv-text:${c.text_color || '#1A1A1A'}`,
        `--pv-surface:${c.surface_color || '#FFFFFF'}`,
        `--pv-topbar-bg:${o.topbar_bg || c.primary_color || '#B22222'}`,
        `--pv-topbar-text:${o.topbar_text || '#FFFFFF'}`,
        `--pv-nav-bg:${o.navbar_bg || 'rgba(255,255,255,0.95)'}`,
        `--pv-hero-bg:${heroBg}`,
        `--pv-hero-bg-image:${heroImage}`,
        `--pv-body-bg-image:${bodyImage}`,
      ].join(';');
    },

    get previewDecorAssets() {
      return (this.assets || []).filter((a) => a.url && ['header-top', 'hero-right', 'hero-left', 'hero-badge'].includes(a.position));
    },

    init() {
      try {
        const cfg = JSON.parse(this.$el.getAttribute('data-config') || '{}');
        this.themeId = cfg.themeId || null;
        this.listUrl = cfg.listUrl || '/';
        this.editBase = cfg.editBase || '';
        this.homeUrl = cfg.homeUrl || '/';
      } catch (e) {
        console.error(e);
      }

      if (this.themeId) {
        this.loadTheme();
      } else {
        this.loading = false;
      }
    },

    async loadTheme() {
      this.loading = true;
      try {
        const { data } = await axios.get(`/api/v1/kasir/engagement/seasonal-themes/${this.themeId}`);
        this.applyTheme(data.data);
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memuat tema.', 'error');
      } finally {
        this.loading = false;
      }
    },

    applyTheme(t) {
      this.themeId = t.id;
      this.assets = t.assets || [];
      this.form = {
        name: t.name || '',
        slug: t.slug || '',
        description: t.description || '',
        type: t.type || 'seasonal',
        status: t.status || 'draft',
        priority: t.priority ?? 0,
        start_at_local: toLocalInput(t.start_at),
        end_at_local: toLocalInput(t.end_at),
        config: {
          ...structuredClone(DEFAULT_CONFIG),
          ...(t.config || {}),
          component_overrides: {
            ...DEFAULT_OVERRIDES,
            ...(t.config?.component_overrides || {}),
          },
        },
        effects: (t.effects || []).map((e) => ({
          effect_type: e.effect_type || 'none',
          is_enabled: e.is_enabled !== false,
          config: e.config || {},
        })),
      };
    },

    addEffect() {
      this.form.effects.push({ effect_type: 'confetti', is_enabled: true, config: { density: 'low' } });
    },

    buildPayload() {
      return {
        name: this.form.name,
        slug: this.form.slug,
        description: this.form.description,
        type: this.form.type,
        status: this.form.status,
        priority: this.form.priority,
        start_at: fromLocalInput(this.form.start_at_local),
        end_at: fromLocalInput(this.form.end_at_local),
        config: this.form.config,
        effects: this.form.effects.filter((e) => e.effect_type && e.effect_type !== 'none'),
      };
    },

    async save() {
      this.saving = true;
      try {
        const body = this.buildPayload();
        let res;
        if (this.themeId) {
          res = await axios.put(`/api/v1/kasir/engagement/seasonal-themes/${this.themeId}`, body);
        } else {
          res = await axios.post('/api/v1/kasir/engagement/seasonal-themes', body);
        }
        this.applyTheme(res.data.data);
        window.toast?.(res.data.message || 'Tema disimpan.', 'success');

        if (!window.location.pathname.includes('/edit')) {
          const url = this.editBase.replace('__ID__', this.themeId);
          if (url && url !== '#') window.location.href = url;
        }
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menyimpan.', 'error');
      } finally {
        this.saving = false;
      }
    },

    previewBackgroundUrl(layer) {
      const path = this.form.config?.component_overrides?.[`${layer}_bg_image`];
      if (!path) return '';
      if (path.startsWith('http://') || path.startsWith('https://')) return path;
      const base = (window.APP_URL || '').replace(/\/$/, '');
      return `${base}/${path.replace(/^\//, '')}`;
    },

    clearBackground(layer) {
      const key = `${layer}_bg_image`;
      this.form.config.component_overrides[key] = '';
    },

    async uploadBackground(event, layer) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !this.themeId) {
        window.toast?.('Simpan tema terlebih dahulu sebelum upload background.', 'error');
        return;
      }

      this.uploadingBg = true;
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('type', 'background');
        fd.append('position', `${layer}-background`);
        const { data } = await axios.post(`/api/v1/kasir/engagement/seasonal-themes/${this.themeId}/assets`, fd);
        const imagePath = data.data?.file_path || '';
        this.form.config.component_overrides[`${layer}_bg_image`] = imagePath;
        if (data.data?.id) {
          const existing = this.assets.findIndex((a) => a.position === `${layer}-background`);
          if (existing >= 0) {
            this.assets[existing] = data.data;
          } else {
            this.assets.push(data.data);
          }
        }
        window.toast?.(data.message || 'Background diunggah.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal mengunggah background.', 'error');
      } finally {
        this.uploadingBg = false;
      }
    },

    async uploadAsset(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file || !this.themeId) return;

      this.uploading = true;
      try {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('type', this.uploadType);
        if (this.uploadPosition) {
          fd.append('position', this.uploadPosition);
        }
        const { data } = await axios.post(`/api/v1/kasir/engagement/seasonal-themes/${this.themeId}/assets`, fd);
        this.assets.push(data.data);
        window.toast?.(data.message || 'Asset diunggah.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal mengunggah asset.', 'error');
      } finally {
        this.uploading = false;
      }
    },

    async updateAssetMeta(asset) {
      if (!this.themeId || !asset?.id) return;
      try {
        const { data } = await axios.patch(
          `/api/v1/kasir/engagement/seasonal-themes/${this.themeId}/assets/${asset.id}`,
          {
            position: asset.position || null,
            opacity: asset.opacity,
          },
        );
        const idx = this.assets.findIndex((a) => a.id === asset.id);
        if (idx >= 0) {
          this.assets[idx] = { ...this.assets[idx], ...data.data };
        }
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal memperbarui asset.', 'error');
      }
    },

    async deleteAsset(asset) {
      if (!window.confirm('Hapus asset ini?')) return;
      try {
        const { data } = await axios.delete(`/api/v1/kasir/engagement/seasonal-themes/${this.themeId}/assets/${asset.id}`);
        this.assets = this.assets.filter((a) => a.id !== asset.id);
        window.toast?.(data.message || 'Asset dihapus.', 'success');
      } catch (e) {
        window.toast?.(e.response?.data?.message || 'Gagal menghapus asset.', 'error');
      }
    },
  };
}

Alpine.data('kasirSeasonalThemeBuilder', createKasirSeasonalThemeBuilder);
